<template>
    <div class="user-profile">

        <status-message></status-message>

        <modal v-if="showRemoveProfilePicture == true" @close="showRemoveProfilePicture = !showRemoveProfilePicture" hide-footer>
            <h4 class="td-h4">Are you sure you want to remove the profile picture?</h4>
            <div class="actions mt-4">
                <button @click="removeProfilePicture()" class="td-btn danger mr-2 icon-left td-text-right"><icon name="faTrashCan" type="solid"></icon> Remove Profile Picture</button>
            </div>
        </modal>

        <modal v-if="showCsrAdd == true" @close="showCsrAdd = !showCsrAdd" hide-footer>
            <h4 class="td-h4">Are you sure you want to promote this user to TruckDown CSR?</h4>
            <p>Promoting this user to TruckDown CSR will allow the user to complete actions such as, editing locations/subscriptions, running transcations, and logging in as customers.</p>
            <p>This permission can always be removed at a later time.</p>
            <div class="actions">
                <button @click="addClaim('role', 'TruckDown CSR')" class="td-btn secondary mr-2 icon-left td-text-right"><icon name="faPlus" type="solid"></icon> TruckDown CSR</button>
            </div>
        </modal>

        <modal v-if="showCsrRemove == true" @close="showCsrRemove = !showCsrRemove" hide-footer>
            <h4 class="td-h4">Are you sure you want to demote this user from TruckDown CSR?</h4>
            <p>Demoting this user from TruckDown CSR will remove the user's ability to complete actions such as, editing locations/subscriptions, running transcations, and logging in as customers.</p>
            <p>This permission can always be added at a later time.</p>
            <div class="actions">
                <button @click="removeClaim('role', 'TruckDown CSR')" class="td-btn danger mr-2 icon-left td-text-right"><icon name="faMinus" type="solid"></icon> TruckDown CSR</button>
            </div>
        </modal>

        <modal v-if="showSystemAdminAdd == true" @close="showSystemAdminAdd = !showSystemAdminAdd" hide-footer>
            <h4 class="td-h4">Are you sure you want to promote this user to System Admin?</h4>
            <p>Promoting this user to System Admin will allow the user complete access to everything in the system and ability to perform any action.</p>
            <p>This is typically only given to management and the IT Team.</p>
            <p>This permission can only be removed by reaching out to the IT Team.</p>
            <div class="actions">
                <button @click="addClaim('role', 'System Admin')" class="td-btn secondary mr-2 icon-left td-text-right"><icon name="faPlus" type="solid"></icon> System Admin</button>
            </div>
        </modal>

        <modal v-if="showDeleteUserModal == true" @close="showDeleteUserModal = !showDeleteUserModal" hide-footer>
            <h4 class="td-h4">Are you sure you want to delete this user?</h4>
            <div class="actions mt-4">
                <button @click="changeStatusSaveCheck(true)" class="td-btn danger mr-2 icon-left td-text-right"><icon name="faTrashCan" type="solid"></icon> Delete User</button>
            </div>
        </modal>

        <modal v-if="showClaims == true" @close="showClaims = false" hide-footer>
            <h4>
                Claims
            </h4>
            <div v-if="userProfile.claims.length > 0" v-for="claim in userProfile.claims" class="ma-2">
                <label><strong>Type: </strong>{{ claim.type }}</label><br />
                <label><strong>Value: </strong></label>{{ claim.value }}
            </div>
            <div v-else>
                <p>User has no claims</p>
            </div>
        </modal>

        <modal v-if="showSettings == true" @close="showSettings = false" hide-footer>
            <h4>Settings</h4>
            <div v-if="Object.keys(userProfile.settings).length > 0" v-for="(value, key) in userProfile.settings" class="ma-2">
                <label><strong>{{key}}</strong></label>: {{ value }}
            </div>
            <div v-else>
                <p>User has no settings</p>
            </div>
        </modal>

        <div v-if="showMoveUserAdmin">
            <move-user-admin :user-profile="userProfile" @cancel="cancelMoveUserAdmin" @update="updateUser"></move-user-admin>
        </div>

        <div v-if="showEditAdminSettings">
            <edit-admin-settings :user-profile="userProfile" @cancel="cancelEditAdminSettings" @update="updateUser"></edit-admin-settings>
        </div>

        <div v-if="showEditUserInfo">
            <edit-user-info :user-profile="userProfile" @cancel="cancelEditUserInfo" @update="updateUser"></edit-user-info>
        </div>

        <div v-if="showEditUserPreferences">
            <edit-user-preferences :user-profile="userProfile" :time-zones="timeZones" :all-services="allServices" @cancel="cancelEditUserPreferences" @update="updateUser"></edit-user-preferences>
        </div>

        <div v-if="showEditSecuritySettings">
            <edit-security-settings :user-profile="userProfile" @cancel="cancelEditSecuritySettings" @update="updateUser"></edit-security-settings>
        </div>

        <div v-if="showEditEmailAddress" class="td-container">
            <edit-email-address :is-admin="isCsrOrAdmin" :user-profile="userProfile" @cancel="cancelEditEmailAddress" @update="updateUser"></edit-email-address>
        </div>

        <div v-if="showEditMobileNumber" class="td-container">
            <edit-mobile-number :is-admin="isCsrOrAdmin" :user-profile="userProfile" @cancel="cancelEditMobileNumber" @update="updateUser"></edit-mobile-number>
        </div>

        <div v-if="showChangePassword" class="td-container">
            <change-password :user="userProfile" @cancel="cancelChangePassword" @update="updateUser"></change-password>
        </div>

        <div class="user-profile-heading">
            <div>
                <img :src="getProfilePicture(userProfile)" class="profile-picture" />
                <span v-if="displayProfilePictureButtons(userProfile)" style="vertical-align: top;">
                    <label for="profile-picture-upload">
                        <icon class="ml-1 td-h4 td-text-primary td-link" name="faUpload" type="solid"></icon>
                        <input id="profile-picture-upload" type="file" accept="image/*" @change="uploadImage($event)" style="display: none;" />
                    </label>
                    <label v-if="userProfile.properties.profilePicture" for="profile-picture-remove">
                        <icon class="ml-1 td-h4 td-text-primary td-link" @click="showRemoveProfilePicture = !showRemoveProfilePicture" name="faTrashCan" type="solid"></icon>
                    </label>
                </span>
            </div>
            <div class="mx-5">
                <p class="td-h3">
                    <strong>{{ userProfile.firstName }} {{ userProfile.lastName }}</strong>
                </p>
                <p>
                    <strong>Username:</strong> {{ userProfile.username }}
                </p>
                <p>
                    <strong>Email:</strong> {{ userProfile.email }}
                </p>
                <p>
                    <strong>Mobile:</strong> {{ formatPhoneString(userProfile.mobile) }}
                </p>

                <div v-if="!isCsrOrAdmin" class="editable-field info-card pa-1" style="padding: 0;">
                    <p>
                        <strong>Status:</strong> <span v-if="!isStatusEditing"> {{ getUserStatus() }} </span>
                    </p>
                    <icon v-if="!isStatusEditing && isStatusEditable" @click="startEditingStatus" type="solid" name="faPen" class="td-text-primary edit-badge"></icon>
                    <div v-if="isStatusEditing" class="td-autocomplete">
                        <icon @click="isStatusEditing = !isStatusEditing" type="solid" name="faXmark" class="td-text-danger edit-badge"></icon>
                        <icon @click="changeStatusSaveCheck(false)" typeof="solid" name="faCheck" class="td-text-success edit-badge"></icon>

                        <select v-model="changeStatusModel.status">
                            <option :value="UserStatus.Active">
                                Active
                            </option>
                            <option :value="UserStatus.Inactive">
                                Inactive
                            </option>
                            <option :value="UserStatus.Purge" style="color: red;">
                                Delete
                            </option>
                        </select>
                    </div>
                </div>
            </div>

            <div class="mx-5" v-if="isCsrOrAdmin">
                <ul class="td-list">
                    <li class="list-item ma-1">
                        <a :href="'/accounts-admin/view-user/tasks/' + userProfile.id + '?Skip=0&Take=10&Sort=&Filter='" class="td-link no-underline"><icon class="mr-1 td-text-primary" name="faListCheck" type="solid"></icon> New Task</a>
                    </li>
                    <li class="list-item ma-1">
                        <a :href="'/emails-admin/edit-email?userid=' + userProfile.id + '&returnUrl=' + emailReturnUrl" class="td-link no-underline"><icon class="mr-1 td-text-primary" name="faEnvelope" type="solid"></icon> Email</a>
                    </li>
                    <li class="list-item ma-1">
                        <a :href="'/analytics/admin/u/' + userProfile.id" class="td-link no-underline"><icon class="mr-1 td-text-primary" name="faChartLine" type="solid"></icon> Fleet Analytics</a>
                    </li>
                </ul>
            </div>
            <div class="mx-5" v-if="isCsrOrAdmin">
                <ul class="td-list">
                    <li class="list-item ma-1" v-if="showLoginas(userProfile)">
                        <a @click.prevent="loginAs" class="td-link no-underline"><icon class="mr-1 td-text-primary" name="faSignIn" type="solid"></icon> Login As</a>
                    </li>
                    <li class="list-item ma-1" v-if="showMoveUser(userProfile)">
                        <a href="#" @click.prevent="showMoveUserAdmin = !showMoveUserAdmin" class="td-link no-underline"><icon class="mr-1 td-text-primary" name="faPeopleArrows" type="solid"></icon> Move User</a>
                    </li>
                </ul>
            </div>
            <div class="mx-5" v-if="isCsrOrAdmin">
                <ul class="td-list">
                    <li class="list-item ma-1">
                        <a @click.prevent="showClaims = true"><icon class="mr-1 td-text-primary" name="faCube" type="solid"></icon> Claims</a>
                    </li>
                    <li class="list-item ma-1">
                        <a @click.prevent="showSettings = true"><icon class="mr-1 td-text-primary" name="faCog" type="solid"></icon> Settings</a>
                    </li>
                </ul>
            </div>
        </div>

        <div class="mb-2 td-card border rounded" v-if="isCsrOrAdmin">
            <div>
                <h4 class="card-heading">
                    Admin Settings
                    <span>
                        <icon class="ml-1 td-text-primary td-link" name="faPenToSquare" type="solid" @click="showEditAdminSettings = !showEditAdminSettings"></icon>
                    </span>
                </h4>
            </div>

            <div class="admin-settings">
                <div>
                    <strong class="mr-1">Status:</strong>
                    {{ getStatus(userProfile) }}
                </div>
                <div>
                    <strong class="mr-1">Primary Contact:</strong>
                    <span v-if="userProfile.isPrimary">
                        <icon class="mr-1 td-text-success" name="faCircleCheck" type="solid"></icon>
                    </span>
                    <span v-if="!userProfile.isPrimary">
                        <icon class="mr-1 td-text-danger" name="faXmark" type="solid"></icon>
                    </span>
                </div>
                <div>
                    <strong class="mr-1">Billing Contact:</strong>
                    <span v-if="userProfile.isBilling">
                        <icon class="mr-1 td-text-success" name="faCircleCheck" type="solid"></icon>
                    </span>
                    <span v-if="!userProfile.isBilling">
                        <icon class="mr-1 td-text-danger" name="faXmark" type="solid"></icon>
                    </span>
                </div>
                <div>
                    <strong class="mr-1">Allowed to Rate Vendors:</strong>
                    <span v-if="getAllowedRateVendors(userProfile)">
                        <icon class="mr-1 td-text-success" name="faCircleCheck" type="solid"></icon>
                    </span>
                    <span v-if="!getAllowedRateVendors(userProfile)">
                        <icon class="mr-1 td-text-danger" name="faXmark" type="solid"></icon>
                    </span>
                </div>
            </div>
            <div>
                <p>
                    <strong class="mr-1">Roles:</strong>
                    {{ getUserRoles(userProfile).join(", ") }}
                </p>
            </div>

            <div v-if="isSystemAdmin && userProfile.ain == 1">
                <div v-if="isUserInRole(userProfile, 'System Admin') == false">
                    <button @click="showCsrRemove = !showCsrRemove" class="td-btn danger mr-2 icon-left td-text-left" v-if="isUserInRole(userProfile, 'TruckDown CSR') == true"><icon name="faMinus" type="solid"></icon> TruckDown CSR</button>
                    <button @click="showCsrAdd = !showCsrAdd" class="td-btn secondary mr-2 icon-left td-text-right" v-if="isUserInRole(userProfile, 'TruckDown CSR') == false"><icon name="faPlus" type="solid"></icon> TruckDown CSR</button>
                    <button @click="showSystemAdminAdd = !showSystemAdminAdd" class="td-btn secondary ml-2 icon-left td-text-right" v-if="isUserInRole(userProfile, 'TruckDown CSR') == true"><icon name="faPlus" type="solid"></icon> System Admin</button>
                </div>
            </div>
        </div>

        <div class="profile-cards">

            <div class="td-card border rounded">
                <div class="user-info">
                    <h4 class="card-heading">
                        Contact
                        <span>
                            <icon class="ml-1 td-text-primary td-link" name="faPenToSquare" type="solid" @click="showEditUserInfo = !showEditUserInfo"></icon>
                        </span>
                    </h4>

                    <p>
                        <strong class="mr-1">First Name:</strong>
                        {{ userProfile.firstName }}
                    </p>

                    <p>
                        <strong class="mr-1">Last Name:</strong>
                        {{ userProfile.lastName }}
                    </p>

                    <p>
                        <strong class="mr-1">Office:</strong>
                        {{ formatPhoneString(userProfile.phones.office) }}
                    </p>

                    <p>
                        <strong class="mr-1">Home:</strong>
                        {{ formatPhoneString(userProfile.phones.home) }}
                    </p>

                    <p>
                        <strong class="mr-1">Fax:</strong>
                        {{ formatPhoneString(userProfile.phones.fax) }}
                    </p>
                </div>
            </div>

            <div class="td-card border rounded">
                <div class="preferences">
                    <h4 class="card-heading">
                        Preferences
                        <span>
                            <icon class="ml-1 td-text-primary td-link" name="faPenToSquare" type="solid" @click="showEditUserPreferences = !showEditUserPreferences"></icon>
                        </span>
                    </h4>
                    <p>
                        <strong class="mr-1">Time Zone:</strong>
                        {{ userProfile.properties.timezone }}
                    </p>
                    <p>
                        <strong class="mr-1">Unit of Measure:</strong>
                        {{ getMeasureUnit(userProfile) }}
                    </p>
                    <p>
                        <strong class="mr-1">Receive Newsletters:</strong>
                        <span v-if="getReceiveNewsletters(userProfile)">
                            <icon class="mr-1 td-text-success" name="faCircleCheck" type="solid"></icon>
                        </span>
                        <span v-if="!getReceiveNewsletters(userProfile)">
                            <icon class="mr-1 td-text-danger" name="faXmark" type="solid"></icon>
                        </span>
                    </p>
                    <p>
                        <strong class="mr-1">Receive Reports:</strong>
                        <span v-if="getReceiveReports(userProfile)">
                            <icon class="mr-1 td-text-success" name="faCircleCheck" type="solid"></icon>
                        </span>
                        <span v-if="!getReceiveReports(userProfile)">
                            <icon class="mr-1 td-text-danger" name="faXmark" type="solid"></icon>
                        </span>
                    </p>

                    <div v-if="getAdvancedOptions(userProfile)">
                        <p>
                            <strong class="mr-1">Default Services:</strong>
                            {{ getDefaultSearchServices(userProfile) }}
                        </p>
                        <p>
                            <strong class="mr-1">Search Private Radius:</strong>
                            {{ getSearchPrivateRadius(userProfile) }}
                        </p>

                        <p>
                            <strong class="mr-1">Search Preferred Radius:</strong>
                            {{ getSearchPreferredRadius(userProfile) }}
                        </p>
                        <p>
                            <strong class="mr-1">Hide Managed Services:</strong>
                            <span v-if="userProfile.settings.hide_managed_services == 'True'">
                                <icon class="mr-1 td-text-success" name="faCircleCheck" type="solid"></icon>
                            </span>
                            <span v-if="userProfile.settings.hide_managed_services == 'False'">
                                <icon class="mr-1 td-text-danger" name="faXmark" type="solid"></icon>
                            </span>
                        </p>

                        <p>
                            <strong class="mr-1">Search By Distance:</strong>
                            <span v-if="getSearchByDistance(userProfile)">
                                <icon class="mr-1 td-text-success" name="faCircleCheck" type="solid"></icon>
                            </span>
                            <span v-if="!getSearchByDistance(userProfile)">
                                <icon class="mr-1 td-text-danger" name="faXmark" type="solid"></icon>
                            </span>
                        </p>
                    </div>
                </div>
            </div>
            <div class="td-card border rounded">
                <div>
                    <h4 class="card-heading">
                        Login / Security Information
                        <span v-if="canEditSecuritySettings(userProfile)">
                            <icon class="ml-1 td-text-primary td-link" name="faPenToSquare" type="solid" @click="showEditSecuritySettings = !showEditSecuritySettings"></icon>
                        </span>
                    </h4>
                    <div>
                        <p>
                            <strong class="mr-1">Account Administrator:</strong>
                            <span v-if="userProfile.isAdmin">
                                <icon class="mr-1 td-text-success" name="faCircleCheck" type="solid"></icon>
                            </span>
                            <span v-if="!userProfile.isAdmin">
                                <icon class="mr-1 td-text-danger" name="faXmark" type="solid"></icon>
                            </span>
                        </p>
                        <p>
                            <strong class="mr-1">Allow Ratings:</strong>
                            <span v-if="getAllowRatings(userProfile)">
                                <icon class="mr-1 td-text-success" name="faCircleCheck" type="solid"></icon>
                            </span>
                            <span v-if="!getAllowRatings(userProfile)">
                                <icon class="mr-1 td-text-danger" name="faXmark" type="solid"></icon>
                            </span>
                        </p>
                        <p>
                            <strong class="mr-1">Edit Notes:</strong>
                            <span v-if="getEditNotes(userProfile)">
                                <icon class="mr-1 td-text-success" name="faCircleCheck" type="solid"></icon>
                            </span>
                            <span v-if="!getEditNotes(userProfile)">
                                <icon class="mr-1 td-text-danger" name="faXmark" type="solid"></icon>
                            </span>
                        </p>
                        <p>
                            <strong class="mr-1">Manage Service Network:</strong>
                            <span v-if="getPreferredVendors(userProfile)">
                                <icon class="mr-1 td-text-success" name="faCircleCheck" type="solid"></icon>
                            </span>
                            <span v-if="!getPreferredVendors(userProfile)">
                                <icon class="mr-1 td-text-danger" name="faXmark" type="solid"></icon>
                            </span>
                        </p>
                        <p>
                            <strong class="mr-1">Advanced Preferences:</strong>
                            <span v-if="getAdvancedOptions(userProfile)">
                                <icon class="mr-1 td-text-success" name="faCircleCheck" type="solid"></icon>
                            </span>
                            <span v-if="!getAdvancedOptions(userProfile)">
                                <icon class="mr-1 td-text-danger" name="faXmark" type="solid"></icon>
                            </span>
                        </p>
                    </div>
                    <div>
                        <div class="my-4">
                            <h4 class="card-heading">
                                Email Address
                                <span>
                                    <icon class="ml-1 td-text-primary td-link" name="faPenToSquare" type="solid" @click="showEditEmailAddress = !showEditEmailAddress"></icon>
                                </span>
                            </h4>
                            {{ userProfile.email }}
                        </div>
                        <div class="my-4">
                            <h4 class="card-heading">
                                Mobile Number
                                <span>
                                    <icon class="ml-1 td-text-primary td-link" name="faPenToSquare" type="solid" @click="showEditMobileNumber = !showEditMobileNumber"></icon>
                                </span>
                            </h4>
                            {{ userProfile.mobile ? formatPhoneString(userProfile.mobile) : 'Not Entered' }}
                        </div>
                        <div class="my-4 change-password">
                            <button class="td-btn secondary icon-left" @click="showChangePassword = !showChangePassword">
                                <icon name="faKey" type="solid"></icon>
                                Change Password
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="footer-card td-card border rounded">
            <div>
                <p><strong>Last Modified:</strong></p>
                <p>{{ dateDisplay(userProfile.modified)}}</p>
            </div>
            <div>
                <p><strong>Last Activity:</strong></p>
                <p>{{ dateDisplay(userProfile.lastActivity)}}</p>
            </div>
            <div>
                <p><strong>Last Login:</strong></p>
                <p>{{ dateDisplay(userProfile.lastLogin)}}</p>
            </div>
        </div>
    </div>
</template>
<script>
    import script from './UserProfile';
    export default script;</script>

<style lang="less">
    @import "./UserProfile.less";
</style>

